import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import PropTypes from "prop-types";
import { toastActions } from "@base-provider";
import {
  deleteSavedQueries,
  getSavedQueriesByGridName,
} from "../../services/savedQueryService";
import getStyled from "../../hooks/getStyled";
import SFPreviewQuery from "./previewQuery";
import SFButton from "../button";
import SFTypography from "../typography";
import SFDrawer from "../drawer";
import SFSkeleton from "../skeleton";
import SFSelect from "../select";
import { SFStack } from "../grid";
import { SFCopyIcon, SFEditIcon, SFDeleteOutlineIcon } from "../icons";
import {
  setSelectedQuery,
  setSavedQueriesPanel,
} from "../../redux/reducers/gridLayoutReducer";
import decodeHTMLEntities from "../../helpers/decodeHTMLEntities";

const { styled } = getStyled();

const SFStackStyled = styled(SFStack)(() => ({
  height: "100%",
}));

const ACTIONS = {
  edit: "edit",
  apply: "apply",
  delete: "delete",
  copy: "copy",
};

const SFSavedQueryDrawer = ({ open, gridName, columnDefs }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [savedQueries, setSavedQueries] = useState({ loading: true });
  const [selectedItem, setSelectedItem] = useState(null);

  const closeDrawerHandler = () => {
    dispatch(setSavedQueriesPanel({ gridName, open: false }));
  };

  const onActionHandler = (action) => {
    switch (action) {
    case ACTIONS.apply:
      dispatch(
        setSelectedQuery({
          gridName,
          selected: {
            query: JSON.parse(selectedItem.query),
            name: selectedItem.name,
            apply: true,
          },
        })
      );
      break;
    case ACTIONS.delete:
      const savedQueryId = selectedItem.id;
      deleteSavedQueries([savedQueryId]).then(() => {
        setSavedQueries(savedQueries.filter((sq) => sq.id !== savedQueryId));
        setSelectedItem(null);
        dispatch(
          toastActions.showSuccessMessage(
            t("savedQueryDrawer.messages.deleteSuccessMessage")
          )
        );
      });
      break;
    case ACTIONS.copy:
      dispatch(
        setSelectedQuery({
          gridName,
          selected: {
            query: JSON.parse(selectedItem.query),
            name: t("savedQueryDrawer.copyNameLabel", {
              value: selectedItem.name,
            }),
            apply: false,
          },
        })
      );
      break;
    case ACTIONS.edit:
      dispatch(
        setSelectedQuery({
          gridName,
          selected: {
            query: JSON.parse(selectedItem.query),
            name: selectedItem.name,
            apply: false,
            id: selectedItem.id,
          },
        })
      );
      break;
    default:
      break;
    }
  };

  const Footer = () => (
    <SFStack direction="row" justifyContent="space-between" alignItems="center">
      <SFStack direction="row" spacing={1} alignItems="center">
        <SFButton variant="secondary" onClick={closeDrawerHandler}>
          {t("savedQueryDrawer.cancelButton")}
        </SFButton>
        <SFButton
          variant="primary"
          onClick={() => onActionHandler(ACTIONS.apply)}
          disabled={selectedItem === null}
        >
          {t("savedQueryDrawer.applyButton")}
        </SFButton>
      </SFStack>
      <SFStack direction="row" spacing={1} alignItems="center">
        <SFButton
          variant="secondary"
          onClick={() => onActionHandler(ACTIONS.copy)}
          icon={<SFCopyIcon />}
          disabled={selectedItem === null}
        >
          {t("savedQueryDrawer.copyButton")}
        </SFButton>
        <SFButton
          variant="secondary"
          onClick={() => onActionHandler(ACTIONS.edit)}
          icon={<SFEditIcon />}
          disabled={selectedItem === null}
        >
          {t("savedQueryDrawer.editButton")}
        </SFButton>
        <SFButton
          variant="secondary"
          onClick={() => onActionHandler(ACTIONS.delete)}
          icon={<SFDeleteOutlineIcon />}
          disabled={selectedItem === null}
        >
          {t("savedQueryDrawer.deleteButton")}
        </SFButton>
      </SFStack>
    </SFStack>
  );

  const handleSavedQueryChange = (e) => {
    let item = savedQueries.find((sq) => sq.name === e.value);
    //used decode function to tranform special characters such as &amp; to operators
    setSelectedItem({ ...item, query: decodeHTMLEntities(item.query) });
  };

  useEffect(() => {
    if (gridName) {
      getSavedQueriesByGridName(gridName).then((res) => {
        setSavedQueries(res.data);
      });
    } else {
      setSavedQueries({ loading: true });
      setSelectedItem(null);
    }
  }, [gridName]);

  return (
    <SFDrawer
      open={open}
      size="m"
      onClose={closeDrawerHandler}
      header={t("savedQueryDrawer.header")}
      footer={<Footer />}
    >
      <SFStackStyled spacing={1}>
        <SFTypography variant="h4">
          {t("savedQueryDrawer.savedQueriesHeader")}
        </SFTypography>
        {savedQueries.loading ? (
          <SFSkeleton />
        ) : (
          <SFSelect
            disablePortal
            onChangeFilter={handleSavedQueryChange}
            values={savedQueries.map((savedQuery) => savedQuery.name)}
            selectedValue={selectedItem?.name ?? ""}
            label={t("savedQueryDrawer.selectSavedQueryPlaceholder")}
            placeholder={t("savedQueryDrawer.selectSavedQueryPlaceholder")}
          />
        )}
        <SFPreviewQuery savedQuery={selectedItem} columnDefs={columnDefs} />
      </SFStackStyled>
    </SFDrawer>
  );
};

SFSavedQueryDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  gridName: PropTypes.string.isRequired,
  columnDefs: PropTypes.array.isRequired,
};

export default SFSavedQueryDrawer;
